<template>
  <div class="page">
    <el-card shadow="never">
      <div slot="header" class="card-header">
        <span>问题反馈</span>
        <el-select v-model="filterStatus" placeholder="状态" clearable style="width: 140px" @change="handleSearch">
          <el-option v-for="opt in statusOptions" :key="opt.value" :label="opt.label" :value="opt.value" />
        </el-select>
      </div>
      <el-table v-loading="loading" :data="list" border stripe>
        <el-table-column prop="id" label="ID" width="70" />
        <el-table-column prop="userId" label="用户ID" width="90" />
        <el-table-column prop="title" label="标题" min-width="140" show-overflow-tooltip />
        <el-table-column prop="feedbackType" label="类型" width="100" />
        <el-table-column prop="content" label="内容" min-width="160" show-overflow-tooltip />
        <el-table-column prop="contact" label="联系方式" width="140" show-overflow-tooltip />
        <el-table-column prop="status" label="状态" width="100">
          <template slot-scope="{ row }">
            <el-tag :type="statusTagType(row.status)" size="small">{{ labelOf(FEEDBACK_STATUS, row.status) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createdAt" label="提交时间" min-width="160" :formatter="formatTime" />
        <el-table-column label="操作" width="140" fixed="right">
          <template slot-scope="{ row }">
            <el-button type="text" size="small" @click="openDetailDialog(row)">查看详情</el-button>
            <el-button type="text" size="small" @click="openReplyDialog(row)">回复</el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-pagination
        class="pagination"
        background
        layout="total, prev, pager, next"
        :current-page.sync="page"
        :page-size="pageSize"
        :total="total"
        @current-change="handlePageChange"
      />
    </el-card>

    <!-- 详情对话框：展示对话线程 + 回复 -->
    <el-dialog :title="'反馈详情 · FB' + String(detailData.id || '').padStart(3, '0')" :visible.sync="detailVisible" width="680px" top="6vh">
      <div v-if="detailLoading" style="text-align:center;padding:30px 0;">
        <i class="el-icon-loading" style="font-size:20px;"></i>
      </div>
      <div v-else-if="detailData" class="detail-wrapper">
        <!-- 基本信息 -->
        <div class="detail-header">
          <div class="detail-meta">
            <el-tag :type="statusTagType(detailData.status)" size="small">{{ labelOf(FEEDBACK_STATUS, detailData.status) }}</el-tag>
            <span class="detail-type">{{ detailData.feedbackType }}</span>
            <span class="detail-time">{{ formatTime(null, null, detailData.createdAt) }}</span>
          </div>
          <h3 class="detail-title">{{ detailData.title }}</h3>
          <p class="detail-info">
            <span>用户ID: {{ detailData.userId }}</span>
            <span v-if="detailData.contact"> | 联系方式: {{ detailData.contact }}</span>
          </p>
        </div>

        <!-- 对话线程 -->
        <div class="thread-list">
          <!-- 原始提交 -->
          <div class="thread-item thread-user">
            <div class="thread-head">
              <span class="thread-badge badge-user">用户提交</span>
              <span class="thread-time">{{ formatTime(null, null, detailData.createdAt) }}</span>
            </div>
            <p class="thread-content">{{ detailData.content }}</p>
            <div v-if="parseUrls(detailData.attachmentUrls).length" class="thread-att">
              <img v-for="(url, i) in parseUrls(detailData.attachmentUrls)" :key="i"
                v-if="isImage(url)" :src="resolveFileUrl(url)" class="att-thumb" @click="previewImage(url)" />
              <a v-for="(url, i) in parseUrls(detailData.attachmentUrls)" :key="'v'+i"
                v-if="isVideo(url)" :href="resolveFileUrl(url)" target="_blank" class="att-video-link">
                <i class="el-icon-video-play"></i> 视频附件
              </a>
            </div>
          </div>

          <!-- 追问/回复消息 -->
          <div v-for="msg in detailData.messages || []" :key="msg.id"
            class="thread-item" :class="msg.senderType === 2 ? 'thread-admin' : 'thread-user'">
            <div class="thread-head">
              <span class="thread-badge" :class="msg.senderType === 2 ? 'badge-admin' : 'badge-user'">
                {{ msg.senderType === 2 ? '官方回复' : '用户追问' }}
              </span>
              <span class="thread-time">{{ formatTime(null, null, msg.createdAt) }}</span>
            </div>
            <p class="thread-content">{{ msg.content }}</p>
            <div v-if="parseUrls(msg.attachmentUrls).length" class="thread-att">
              <img v-for="(url, i) in parseUrls(msg.attachmentUrls)" :key="i"
                v-if="isImage(url)" :src="resolveFileUrl(url)" class="att-thumb" @click="previewImage(url)" />
              <a v-for="(url, i) in parseUrls(msg.attachmentUrls)" :key="'v'+i"
                v-if="isVideo(url)" :href="resolveFileUrl(url)" target="_blank" class="att-video-link">
                <i class="el-icon-video-play"></i> 视频附件
              </a>
            </div>
          </div>
        </div>

        <!-- 管理员回复输入 -->
        <div class="reply-section">
          <h4>回复内容</h4>
          <el-input v-model="replyForm.reply" type="textarea" :rows="4" placeholder="输入回复内容..." />
          <div class="reply-actions">
            <el-button type="primary" size="small" :loading="replySubmitting" @click="submitReplyFromDetail">提交回复</el-button>
            <el-button v-if="detailData.status !== 2" size="small" @click="markResolved">标记已解决</el-button>
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- 图片预览 -->
    <el-dialog :visible.sync="imagePreviewVisible" width="auto" :show-close="true" append-to-body>
      <img :src="imagePreviewUrl" style="max-width:100%;max-height:80vh;" />
    </el-dialog>
  </div>
</template>

<script>
import { getFeedback, replyFeedback, getFeedbackDetail, resolveFeedback } from '@/api/admin'
import { FEEDBACK_STATUS, labelOf, optionsOf } from '@/utils/adminConstants'
import { resolveFileUrl } from '@/utils/fileUrl'
import listPage from '@/mixins/listPage'

function formatDateTimeStr(value) {
  if (!value) return '-'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return String(value)
  const pad = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

export default {
  name: 'AdminFeedback',
  mixins: [listPage],
  data() {
    return {
      FEEDBACK_STATUS,
      filterStatus: null,
      detailVisible: false,
      detailLoading: false,
      detailData: {},
      replyForm: { id: null, reply: '' },
      replySubmitting: false,
      imagePreviewVisible: false,
      imagePreviewUrl: ''
    }
  },
  computed: {
    statusOptions() {
      return optionsOf(FEEDBACK_STATUS)
    }
  },
  created() {
    this.loadList()
  },
  methods: {
    labelOf,
    resolveFileUrl,
    formatTime(row, col, value) {
      return formatDateTimeStr(value)
    },
    statusTagType(status) {
      if (status === 0) return 'info'
      if (status === 1) return 'warning'
      if (status === 2) return 'success'
      return 'info'
    },
    fetchList(params) {
      return getFeedback({
        ...params,
        status: this.filterStatus != null ? this.filterStatus : undefined
      })
    },
    parseUrls(raw) {
      if (!raw) return []
      return String(raw).split(',').map(s => s.trim()).filter(Boolean)
    },
    isImage(url) {
      return /\.(png|jpe?g|gif|webp)(\?|$)/i.test(url)
    },
    isVideo(url) {
      return /\.(mp4|webm|mov)(\?|$)/i.test(url)
    },
    previewImage(url) {
      this.imagePreviewUrl = resolveFileUrl(url)
      this.imagePreviewVisible = true
    },
    async openDetailDialog(row) {
      this.detailVisible = true
      this.detailLoading = true
      this.replyForm = { id: row.id, reply: '' }
      try {
        this.detailData = await getFeedbackDetail(row.id)
      } catch (e) {
        this.$message.error('加载详情失败')
        this.detailData = {}
      } finally {
        this.detailLoading = false
      }
    },
    openReplyDialog(row) {
      this.replyForm = { id: row.id, reply: row.reply || '' }
      this.openDetailDialog(row)
    },
    async submitReplyFromDetail() {
      if (!this.replyForm.reply.trim()) {
        this.$message.warning('请输入回复内容')
        return
      }
      this.replySubmitting = true
      try {
        await replyFeedback(this.replyForm.id, this.replyForm.reply)
        this.$message.success('回复成功')
        // 重新加载详情
        this.detailData = await getFeedbackDetail(this.replyForm.id)
        this.replyForm.reply = ''
        this.loadList()
      } catch (e) {
        this.$message.error('回复失败')
      } finally {
        this.replySubmitting = false
      }
    },
    async markResolved() {
      try {
        await this.$confirm('确认标记该反馈为已解决吗？', '提示')
        await resolveFeedback(this.detailData.id)
        this.$message.success('已标记为已解决')
        this.detailData = await getFeedbackDetail(this.detailData.id)
        this.loadList()
      } catch (e) {
        if (e !== 'cancel') {
          this.$message.error('操作失败')
        }
      }
    }
  }
}
</script>

<style scoped>
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.pagination {
  margin-top: 16px;
  text-align: right;
}

/* ========== 详情对话框 ========== */
.detail-wrapper {
  max-height: 70vh;
  overflow-y: auto;
}
.detail-header {
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #ebeef5;
}
.detail-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}
.detail-type {
  color: #909399;
  font-size: 13px;
}
.detail-time {
  color: #909399;
  font-size: 13px;
}
.detail-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 6px;
}
.detail-info {
  color: #909399;
  font-size: 13px;
  margin: 0;
}

/* ========== 对话线程 ========== */
.thread-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
}
.thread-item {
  border-radius: 8px;
  padding: 12px 16px;
  border: 1px solid #ebeef5;
}
.thread-user {
  background: #fafafa;
  border-color: #ebeef5;
}
.thread-admin {
  background: #ecf5ff;
  border-color: #d9ecff;
}
.thread-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}
.thread-badge {
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 4px;
}
.badge-user {
  background: #f4f4f5;
  color: #909399;
}
.badge-admin {
  background: #d9ecff;
  color: #409eff;
}
.thread-time {
  font-size: 12px;
  color: #c0c4cc;
}
.thread-content {
  font-size: 14px;
  color: #303133;
  line-height: 1.6;
  margin: 0;
}
.thread-att {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}
.att-thumb {
  width: 80px;
  height: 80px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #ebeef5;
  cursor: pointer;
  transition: transform .2s;
}
.att-thumb:hover {
  transform: scale(1.05);
}
.att-video-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: #f4f4f5;
  border-radius: 6px;
  color: #606266;
  font-size: 13px;
}

/* ========== 回复区 ========== */
.reply-section {
  border-top: 1px solid #ebeef5;
  padding-top: 16px;
}
.reply-section h4 {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 10px;
}
.reply-actions {
  display: flex;
  gap: 10px;
  margin-top: 10px;
}
</style>